import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseClient } from './supabase';

/**
 * A rep associated with a merchant, rolled up to the merchant level.
 * Backed by core_crm.get_merchant_reps (SECURITY DEFINER): returns the main
 * rep plus everyone with access via a merchant-level rep_link or via any of
 * the merchant's submissions. Gated on merchant visibility, so Openers/Closers
 * see every rep on a merchant they can access and nothing on one they can't.
 */
export interface MerchantRep {
  rep: number;
  rep_name: string;
  is_main: boolean;
  via_merchant: boolean;
  via_submission: boolean;
}

export function merchantRepsQueryOptions(merchantId: number) {
  return {
    queryKey: ['merchant_reps', merchantId] as const,
    staleTime: 60_000,
    queryFn: async (): Promise<MerchantRep[]> => {
      // get_merchant_reps is a post-migration RBAC function (supabase/pg/rbac),
      // not part of the pinned generated Database types, so the schema/rpc call
      // is cast narrowly rather than regenerating the whole types file.
      const rpc = (supabaseClient.schema('core_crm') as any).rpc.bind(
        supabaseClient.schema('core_crm')
      );
      const { data, error } = await rpc('get_merchant_reps', { p_merchant: merchantId });
      if (error) throw error;
      return (data ?? []) as MerchantRep[];
    },
  };
}

export function useMerchantReps(merchantId: number | null | undefined) {
  return useQuery<MerchantRep[]>({
    ...merchantRepsQueryOptions(Number(merchantId)),
    enabled: !!merchantId,
  });
}

/** Invalidate the merchant-reps roster after a rep_link add/remove. */
export function useInvalidateMerchantReps() {
  const qc = useQueryClient();
  return async (merchantId: number) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['merchant_reps', merchantId] }),
      qc.invalidateQueries({ queryKey: ['merchant_rep_history', merchantId] }),
    ]);
  };
}

/** One rep_link access-change event for a merchant (REG-497 AC#3). */
export interface MerchantRepHistoryEvent {
  rep_link_id: number;
  rep: number | null;
  rep_name: string | null;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  changed_by: number | null;
  changed_by_name: string | null;
  transaction_time: string;
}

export function useMerchantRepHistory(merchantId: number | null | undefined) {
  return useQuery<MerchantRepHistoryEvent[]>({
    queryKey: ['merchant_rep_history', merchantId],
    enabled: !!merchantId,
    staleTime: 60_000,
    queryFn: async () => {
      const rpc = (supabaseClient.schema('core_crm') as any).rpc.bind(
        supabaseClient.schema('core_crm')
      );
      const { data, error } = await rpc('get_merchant_rep_history', { p_merchant: merchantId as number });
      if (error) throw error;
      return (data ?? []) as MerchantRepHistoryEvent[];
    },
  });
}
